import axios from "axios";

// Video uploads. The hosting proxy rejects any single request over about
// 100MB, so anything bigger than SINGLE_SHOT_BYTES is sent in small pieces
// and stitched back together on the server.
const API_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export const MAX_VIDEO_MB = 200;
const CHUNK_BYTES = 4 * 1024 * 1024;
const SINGLE_SHOT_BYTES = 0; // always send in pieces so every request can be retried

function newUploadId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Upload one video file. Resolves with the same payload as POST /api/uploads
 * ({ id, url, original_name, ... }). onProgress gets a whole number 0 to 100.
 */
export async function uploadVideoFile(file, { entityType = "general", entityId = "", tag = "video", onProgress } = {}) {
  const report = (pct) => { if (onProgress) onProgress(Math.max(0, Math.min(100, Math.round(pct)))); };
  const qs = `entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}&tag=${encodeURIComponent(tag)}`;
  const headers = { "Content-Type": "multipart/form-data" };
  report(0);

  if (file.size <= SINGLE_SHOT_BYTES) {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API_URL}/uploads?${qs}`, fd, {
      headers,
      onUploadProgress: (e) => { if (e.total) report((e.loaded / e.total) * 99); },
    });
    report(100);
    return res.data;
  }

  const total = Math.ceil(file.size / CHUNK_BYTES);
  const uploadId = newUploadId();
  let result = null;
  for (let i = 0; i < total; i++) {
    const part = file.slice(i * CHUNK_BYTES, Math.min(file.size, (i + 1) * CHUNK_BYTES));
    const url = `${API_URL}/uploads/chunk?upload_id=${uploadId}&index=${i}&total=${total}&filename=${encodeURIComponent(file.name)}&${qs}`;
    let attempt = 0;
    for (;;) {
      try {
        const fd = new FormData();
        fd.append("chunk", part, "chunk.bin");
        const res = await axios.post(url, fd, { headers });
        result = res.data;
        break;
      } catch (err) {
        const status = err.response?.status;
        // Retry network drops and server hiccups; give up at once on a real rejection
        const retryable = !status || status >= 500 || status === 408 || status === 429;
        if (!retryable || attempt >= 7) throw err;
        attempt += 1;
        await wait(Math.min(800 * attempt, 5000));
      }
    }
    report(((i + 1) / total) * 99);
  }
  report(100);
  return result;
}
